import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import arrow from "../assets/arrow.png";
import { url, ak } from "../pages/HomePage";

function WorkHoursPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const [workHours, setWorkHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMonths, setOpenMonths] = useState({});

  useEffect(() => {
    if (!state || !state.id) {
      setLoading(false);
      return;
    }

    fetch(url + `/admin/user/${state.id}/work-hours` + ak, {
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Työtuntien haku epäonnistui");
        }
        return response.json();
      })
      .then((data) => {
        const months = Array.isArray(data) ? data : [];
        setWorkHours(months);

        setOpenMonths({});
      })
      .catch((error) => {
        console.error(error);
        setWorkHours([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [state]);

  function formatMinutes(minutes) {
    const totalMinutes = Math.max(0, Math.round(Number(minutes) || 0));
    const hours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;

    if (hours === 0) {
      return `${remainingMinutes} min`;
    }

    if (remainingMinutes === 0) {
      return `${hours} h`;
    }

    return `${hours} h ${remainingMinutes} min`;
  }

  function formatMonth(monthKey) {
    const [year, month] = monthKey.split("-");

    return new Date(
      Number(year),
      Number(month) - 1,
      1
    ).toLocaleDateString("fi-FI", {
      month: "long",
      year: "numeric",
    });
  }

  function formatDate(dateString) {
    const [year, month, day] = dateString.split("-");
    return `${Number(day)}.${Number(month)}.${year}`;
  }

  function formatTime(dateString) {
    if (!dateString) {
      return "";
    }

    const date = new Date(dateString);

    return date.toLocaleTimeString("fi-FI", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Helsinki",
    });
  }

  function printWorkHours() {
    window.print();
  }

  if (!state || !state.id) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-[90%]">
        <h1 className="text-3xl font-bold">Työtunnit</h1>
        <p>Työntekijän tietoja ei löytynyt.</p>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-center w-full min-h-[90%] py-16">
      <img
        src={arrow}
        alt="takaisin"
        className="absolute h-6 cursor-pointer top-16 left-10"
        onClick={() => navigate(-1)}
      />

      <div className="w-[92%] max-w-[1500px]">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold">Työtunnit</h1>
            <p className="mt-2 text-xl">{state.fullname}</p>
          </div>

          {!loading && workHours.length > 0 && (
            <button
              type="button"
              onClick={printWorkHours}
              className="print:hidden bg-main hover:bg-zinc-300 text-white font-bold py-2 px-5 border-0 rounded"
            >
              Tulosta
            </button>
          )}
        </div>

        {loading ? (
          <p>Ladataan...</p>
        ) : workHours.length === 0 ? (
          <div className="p-6 card">
            <p>Ei työtunteja.</p>
          </div>
        ) : (
          workHours.map((month) => (
            <div
              key={month.month}
              className={
                "p-6 mb-8 card " +
                (openMonths[month.month] ? "" : "print:hidden")
              }
            >
              <div
                className="flex items-center justify-between p-3 mb-3 bg-gray-200 rounded cursor-pointer"
                onClick={() =>
                  setOpenMonths((current) =>
                    current[month.month]
                      ? {}
                      : { [month.month]: true }
                  )
                }
              >
                <div>
                  <h2 className="text-xl font-bold capitalize">
                    {formatMonth(month.month)}
                  </h2>
                  <div className="mt-1 text-sm">
                    Tehdyt tunnit yhteensä:{" "}
                    <span className="font-bold">
                      {formatMinutes(month.totals.base_minutes)}
                    </span>
                  </div>
                </div>
                <span className="px-3 text-2xl font-bold">
                  {openMonths[month.month] ? "−" : "+"}
                </span>
              </div>

              <div
                className={
                  (openMonths[month.month] ? "block " : "hidden ") +
                  "w-full overflow-x-auto"
                }
              >
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="p-3">Päivä / keikka</th>
                      <th className="p-3">Työaika</th>
                      <th className="p-3">Tehdyt tunnit</th>
                      <th className="p-3">Ilta 18–21</th>
                      <th className="p-3">Yö 21–06</th>
                      <th className="p-3">Lauantai</th>
                      <th className="p-3">Sunnuntai/pyhä</th>
                    </tr>
                  </thead>

                  <tbody>
                    {month.rows.map((row, index) => (
                      <tr
                        key={`${row.gig_id}-${row.date}-${index}`}
                        className="border-b"
                      >
                        <td className="p-3">
                          <div className="font-bold">
                            {formatDate(row.date)}
                          </div>
                          <div>{row.title}</div>
                        </td>

                        <td className="p-3 whitespace-nowrap">
                          {formatTime(row.start)}–{formatTime(row.end)}
                        </td>

                        <td className="p-3">
                          {formatMinutes(row.base_minutes)}
                        </td>
                        <td className="p-3">
                          {formatMinutes(row.evening_minutes)}
                        </td>
                        <td className="p-3">
                          {formatMinutes(row.night_minutes)}
                        </td>
                        <td className="p-3">
                          {formatMinutes(row.saturday_minutes)}
                        </td>
                        <td className="p-3">
                          {formatMinutes(row.sunday_holiday_minutes)}
                        </td>
                      </tr>
                    ))}

                    <tr className="font-bold bg-gray-100">
                      <td className="p-3">Yhteensä</td>
                      <td className="p-3"></td>
                      <td className="p-3">
                        {formatMinutes(month.totals.base_minutes)}
                      </td>
                      <td className="p-3">
                        {formatMinutes(month.totals.evening_minutes)}
                      </td>
                      <td className="p-3">
                        {formatMinutes(month.totals.night_minutes)}
                      </td>
                      <td className="p-3">
                        {formatMinutes(month.totals.saturday_minutes)}
                      </td>
                      <td className="p-3">
                        {formatMinutes(month.totals.sunday_holiday_minutes)}
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default WorkHoursPage;
